import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import InfoTooltip from './InfoTooltip';
import AIAnalysisSection from './AIAnalysisSection';
import ManagementNotes from './ManagementNotes';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Line,
  AreaChart,
  Area,
  ReferenceLine,
  Cell
} from 'recharts';
import { RefreshCw, LogOut, TrendingDown, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { Navigation } from './Navigation';

// API is now imported from utils

// Simple card component with optional tooltip
const SectionCard = ({ title, children, tooltip }) => (
  <div className="bg-[#1A1A1A] rounded-lg p-4">
    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
      <h3 className="text-sm font-medium text-white">{title}</h3>
      {tooltip}
    </div>
    {children}
  </div>
);

// Format number with Danish locale
const formatDKK = (value) => {
  return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Stable chart wrapper to prevent DOM issues
const StableChartContainer = ({ children, height = 120 }) => (
  <div style={{ width: '100%', height }}>
    <ResponsiveContainer width="100%" height="100%">
      {children}
    </ResponsiveContainer>
  </div>
);

// Spend Chart Component - shows daily spend for current week
const SpendChart = React.memo(({ data, totalSpend }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[120px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  return (
    <StableChartContainer height={120}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="dag" 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} 
        />
        <YAxis 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          tickFormatter={(v) => `${(v/1000).toFixed(0)}k`}
          width={35}
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: '#1A1A1A', 
            border: '1px solid rgba(255,255,255,0.2)', 
            borderRadius: '8px',
            color: '#fff'
          }}
          labelStyle={{ color: '#fff' }}
          itemStyle={{ color: '#fff' }}
          formatter={(value, name) => {
            if (value === null) return ['Ingen data', ''];
            const formattedValue = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            return [`${formattedValue} kr`, 'Dagligt spend'];
          }}
        />
        <Bar 
          dataKey="spend" 
          fill="#C77700" 
          name="spend"
          radius={[4, 4, 0, 0]}
        />
      </ComposedChart>
    </StableChartContainer>
  );
});

// Cost Per Booking Chart Component - shows cost per new booking per day
const CostPerBookingChart = React.memo(({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[120px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  // Find max value for Y axis
  const maxCost = Math.max(...data.filter(d => d.costPerBooking !== null).map(d => d.costPerBooking), 1000);
  const yAxisMax = Math.ceil(maxCost / 500) * 500; // Round up to nearest 500

  return (
    <StableChartContainer height={120}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="dag" 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} 
        />
        <YAxis 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          domain={[0, yAxisMax]}
          tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v}
          width={35}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#ffffff' }}
          labelStyle={{ color: '#ffffff' }}
          itemStyle={{ color: '#ffffff' }}
          formatter={(value, name) => {
            if (value === null || value === undefined) return ['Ingen data', ''];
            if (name === 'bookinger') return [value, 'Nye bookinger'];
            const formattedValue = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            return [`${formattedValue} kr`, 'Pris pr. booking'];
          }}
        />
        <ReferenceLine y={1200} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" label={{ value: 'Mål: 1.200', fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
        <Bar 
          dataKey="costPerBooking" 
          name="costPerBooking"
          radius={[4, 4, 0, 0]}
        >
          {data.map((entry, index) => {
            let color = '#2E7D32'; // Green - good (≤ 1.200 kr)
            if (entry.costPerBooking > 1500) color = '#B3261E'; // Red - too expensive (> 1.500 kr)
            else if (entry.costPerBooking > 1200) color = '#C77700'; // Orange - warning (1.200-1.500 kr)
            return <Cell key={`cell-${index}`} fill={entry.costPerBooking !== null ? color : 'transparent'} />;
          })}
        </Bar>
      </ComposedChart>
    </StableChartContainer>
  );
});

// Coverage Per Order Chart Component - shows average coverage per order per day
const CoveragePerOrderChart = React.memo(({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[120px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  // Find max value for Y axis
  const maxCoverage = Math.max(...data.filter(d => d.avg_coverage !== null).map(d => d.avg_coverage), 5000);
  const yAxisMax = Math.ceil(maxCoverage / 1000) * 1000; // Round up to nearest 1000

  return (
    <StableChartContainer height={120}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="dag" 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} 
        />
        <YAxis 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          domain={[0, yAxisMax]}
          tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
          width={35}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#ffffff' }}
          labelStyle={{ color: '#ffffff' }}
          itemStyle={{ color: '#ffffff' }}
          formatter={(value, name) => {
            if (value === null || value === undefined) return ['Ingen data', ''];
            if (name === 'order_count') return [value, 'Antal opmålinger'];
            const formattedValue = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            return [`${formattedValue} kr`, 'Gns. dækning pr. opmåling'];
          }}
        />
        <ReferenceLine y={4500} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" label={{ value: 'Mål: 4.500', fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
        <Bar 
          dataKey="avg_coverage" 
          name="avg_coverage"
          radius={[4, 4, 0, 0]}
        >
          {data.map((entry, index) => {
            let color = '#2E7D32'; // Green - good (≥ 4.500 kr)
            if (entry.avg_coverage !== null) {
              if (entry.avg_coverage < 3500) color = '#B3261E'; // Red - too low
              else if (entry.avg_coverage < 4500) color = '#C77700'; // Orange - warning
            }
            return <Cell key={`cell-${index}`} fill={entry.avg_coverage !== null ? color : 'transparent'} />;
          })}
        </Bar>
      </ComposedChart>
    </StableChartContainer>
  );
});

// Profitability Monthly Chart - shows profit per opmåling over last 6 months
const ProfitabilityMonthlyChart = React.memo(({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[160px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  // Find min/max for Y axis
  const values = data.map(d => d.profit_per_opmaling);
  const minValue = Math.min(...values, 0);
  const maxValue = Math.max(...values, 1000);
  const yAxisMin = Math.floor(minValue / 500) * 500;
  const yAxisMax = Math.ceil(maxValue / 500) * 500;

  return (
    <StableChartContainer height={160}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="month_short" 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
        />
        <YAxis 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          domain={[yAxisMin, yAxisMax]}
          tickFormatter={(v) => v >= 1000 || v <= -1000 ? `${(v/1000).toFixed(1)}k` : v}
          width={40}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
          labelStyle={{ color: '#ffffff' }}
          itemStyle={{ color: '#ffffff' }}
          formatter={(value, name, props) => {
            const formattedValue = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            return [`${formattedValue} kr`, 'Profit pr. opmåling'];
          }}
          labelFormatter={(label, payload) => {
            if (payload && payload[0]) {
              return payload[0].payload.month;
            }
            return label;
          }}
          cursor={{ fill: 'rgba(255,255,255,0.1)' }}
        />
        <ReferenceLine y={0} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" />
        <Bar 
          dataKey="profit_per_opmaling" 
          name="profit_per_opmaling"
          radius={[4, 4, 0, 0]}
        >
          {data.map((entry, index) => {
            const color = entry.profit_per_opmaling >= 0 ? '#2E7D32' : '#B3261E';
            return <Cell key={`cell-${index}`} fill={color} />;
          })}
        </Bar>
      </ComposedChart>
    </StableChartContainer>
  );
});

// Nye Bookinger Chart Component - completely isolated (akkumuleret)
const NyeBookingerChart = React.memo(({ data, weeklyTarget }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[120px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  // Find the maximum value from data to set appropriate Y-axis domain
  const maxAkkumuleret = Math.max(...data.map(d => d.akkumuleret || 0));
  const maxMaal = Math.max(...data.map(d => d.maal || 0));
  const maxValue = Math.max(maxAkkumuleret, maxMaal, weeklyTarget || 0);
  const yAxisMax = Math.ceil((maxValue + 20) / 10) * 10; // Round up to nearest 10 with some padding

  return (
    <StableChartContainer height={120}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="dag" 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} 
        />
        <YAxis 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          domain={[0, yAxisMax]}
          width={30}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
          formatter={(value, name) => [value ?? 'N/A', name === 'akkumuleret' ? 'Resultat' : 'Mål']}
        />
        <Bar 
          dataKey="akkumuleret" 
          fill="#1E6F7A" 
          name="akkumuleret"
          radius={[4, 4, 0, 0]}
        />
        <Line 
          type="monotone" 
          dataKey="maal" 
          stroke="#B3261E" 
          strokeWidth={2}
          dot={{ fill: '#B3261E', strokeWidth: 2, r: 3 }}
          name="maal"
        />
      </ComposedChart>
    </StableChartContainer>
  );
});

// Daglige Nye Bookinger Chart Component - viser nye bookinger pr. dag (ikke akkumuleret)
const DagligeNyeBookingerChart = React.memo(({ data, dailyTarget }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[120px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  // Use the dailyTarget prop passed from parent (region-specific)
  const target = dailyTarget || Math.round(252 / 7);
  
  // Calculate max value for Y-axis
  const maxBookinger = Math.max(...data.map(d => d.nye_bookinger || 0));
  const yAxisMax = Math.max(maxBookinger, target) + 5;

  return (
    <StableChartContainer height={120}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="dag" 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} 
        />
        <YAxis 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          domain={[0, yAxisMax]}
          width={30}
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: '#1A1A1A', 
            border: '1px solid rgba(255,255,255,0.2)', 
            borderRadius: '8px',
            color: '#fff'
          }}
          labelStyle={{ color: '#fff' }}
          itemStyle={{ color: '#fff' }}
          formatter={(value, name) => [value ?? 'N/A', 'Nye bookinger']}
        />
        <Bar 
          dataKey="nye_bookinger" 
          name="nye_bookinger"
          radius={[4, 4, 0, 0]}
        >
          {data.map((entry, index) => {
            const color = entry.nye_bookinger >= target ? '#2E7D32' : entry.nye_bookinger >= target * 0.7 ? '#C77700' : '#B3261E';
            return <Cell key={`cell-${index}`} fill={entry.nye_bookinger !== null ? color : 'transparent'} />;
          })}
        </Bar>
        <ReferenceLine 
          y={target} 
          stroke="#B3261E" 
          strokeDasharray="5 5" 
          strokeWidth={2}
        />
      </ComposedChart>
    </StableChartContainer>
  );
});

// KPT History Chart Component - completely isolated
const KptHistoryChart = React.memo(({ historyData, currentKpt, targetKpt, snapshotsAvailable }) => {
  const kptColor = currentKpt >= 0.45 ? '#2E7D32' : currentKpt >= 0.35 ? '#C77700' : '#B3261E';
  
  // Calculate trend
  const validData = historyData.filter(d => d.kpt !== null);
  let trend = 0;
  if (validData.length >= 2) {
    const first = validData[0].kpt;
    const last = validData[validData.length - 1].kpt;
    trend = ((last - first) / first) * 100;
  }
  const trendColor = trend >= 0 ? '#2E7D32' : '#B3261E';

  // Prepare chart data with embedded target
  const chartData = historyData.map(d => ({
    ...d,
    target: targetKpt
  }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-3xl font-bold" style={{ color: kptColor }}>{currentKpt.toFixed(3)}</p>
          <p className="text-xs text-white/60">Nuværende KPT (30d frem)</p>
        </div>
        {validData.length >= 2 && (
          <div className="text-right">
            <div className="flex items-center gap-2" style={{ color: trendColor }}>
              {trend < 0 ? <TrendingDown className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />}
              <span className="text-xl font-bold">{trend > 0 ? '+' : ''}{trend.toFixed(1)}%</span>
            </div>
            <p className="text-xs text-white/60">vs. første måling</p>
          </div>
        )}
      </div>
      
      <div className="h-28">
        {validData.length > 0 ? (
          <StableChartContainer height={112}>
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="kptGradientPipeline" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={kptColor} stopOpacity={0.4}/>
                  <stop offset="95%" stopColor={kptColor} stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="label" stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
              <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} domain={[0, 0.6]} tickFormatter={(v) => v.toFixed(3)} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} 
                formatter={(value) => value ? [value.toFixed(3), 'KPT'] : ['Ingen data', '']} 
              />
              <ReferenceLine y={targetKpt} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" />
              <Area 
                type="monotone" 
                dataKey="kpt" 
                stroke={kptColor} 
                strokeWidth={2} 
                fill="url(#kptGradientPipeline)" 
                dot={{ fill: kptColor, strokeWidth: 2, r: 3 }} 
                connectNulls={true} 
              />
            </AreaChart>
          </StableChartContainer>
        ) : (
          <div className="h-full flex items-center justify-center bg-white/5 rounded-lg">
            <p className="text-sm text-white/40">Historik bygges op over de næste 7 dage...</p>
          </div>
        )}
      </div>
      
      <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/10 text-xs text-white/40">
        <span>--- Mål: {targetKpt.toFixed(3)}</span>
        <span>{snapshotsAvailable}/7 dage med historik</span>
      </div>
    </div>
  );
});

// Booking Flow Gauge Component
const BookingFlowGauge = ({ nyeBookinger, afvikledeBookinger, size = 160 }) => {
  const flow = nyeBookinger - afvikledeBookinger;
  const maxFlow = 50;
  
  const getColor = (val) => {
    if (val >= 10) return '#2E7D32';
    if (val >= 0) return '#C77700';
    return '#B3261E';
  };

  const color = getColor(flow);
  const normalizedFlow = Math.max(-maxFlow, Math.min(maxFlow, flow));
  const percentage = ((normalizedFlow + maxFlow) / (maxFlow * 2)) * 100;
  const angle = (percentage / 100) * 180;
  
  const radius = 45;
  const strokeWidth = 8;
  const center = 50;
  
  const polarToCartesian = (cx, cy, r, ang) => {
    const rad = (ang - 180) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
  };

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.65} viewBox="0 0 100 65">
        <path d={describeArc(center, 55, radius, 0, 180)} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={strokeWidth} strokeLinecap="round" />
        <path d={describeArc(center, 55, radius, 126, 180)} fill="none" stroke="#2E7D32" strokeWidth={strokeWidth} strokeLinecap="round" opacity="0.25" />
        <path d={describeArc(center, 55, radius, 90, 126)} fill="none" stroke="#C77700" strokeWidth={strokeWidth} strokeLinecap="round" opacity="0.25" />
        <path d={describeArc(center, 55, radius, 0, 90)} fill="none" stroke="#B3261E" strokeWidth={strokeWidth} strokeLinecap="round" opacity="0.25" />
        {angle > 0 && (
          <path d={describeArc(center, 55, radius, 0, Math.min(angle, 180))} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
        )}
        <line x1={center} y1="55" x2={center + (radius - 10) * Math.cos((angle - 180) * Math.PI / 180)} y2={55 + (radius - 10) * Math.sin((angle - 180) * Math.PI / 180)} stroke={color} strokeWidth="2" strokeLinecap="round" />
        <circle cx={center} cy="55" r="5" fill={color} />
      </svg>
      <p className="text-2xl font-bold mt-1" style={{ color }}>{flow > 0 ? '+' : ''}{flow}</p>
      <p className="text-xs text-white/60">bookingflow</p>
    </div>
  );
};

export const BookingPipeline = ({ user }) => {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [region, setRegion] = useState('Hele landet');
  const [regions] = useState(['Hele landet', 'Sjælland', 'Fyn', 'Jylland']);
  const [calendarCapacityData, setCalendarCapacityData] = useState(null);
  const [kptHistoryData, setKptHistoryData] = useState(null);
  const [newBookingsData, setNewBookingsData] = useState(null);
  const [cancellationData, setCancellationData] = useState(null);
  const [spendData, setSpendData] = useState(null);
  const [coveragePerDayData, setCoveragePerDayData] = useState(null);
  const [marketingVsCoverageData, setMarketingVsCoverageData] = useState(null);
  const [mktCovPeriod, setMktCovPeriod] = useState('week');  // 'week' | 'month'
  const [showPastWeeks, setShowPastWeeks] = useState(false);  // toggle: false = de 4 kommende uger, true = de 4 tidligere uger
  const [profitabilityData, setProfitabilityData] = useState(null);
  const [profitabilityMonthlyData, setProfitabilityMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profitabilityExpanded, setProfitabilityExpanded] = useState(false);
  const [fetchWarnings, setFetchWarnings] = useState([]);

  // Fetch én endpoint med retry + timeout, så én langsom Google Sheets-kald ikke vælter hele siden
  const fetchOne = useCallback(async (url, retries = 2) => {
    let lastErr = null;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await api.get(url, { timeout: 25000 });
      } catch (e) {
        lastErr = e;
        if (e.response?.status === 401) throw e;                     // auth-fejl — send videre
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, 500 * (attempt + 1))); // 0,5s → 1s backoff
        }
      }
    }
    throw lastErr;
  }, []);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      setFetchWarnings([]);
      const regionParam = region !== 'Hele landet' ? `?region=${encodeURIComponent(region)}` : '';
      const regionParamAmp = region !== 'Hele landet' ? `&region=${encodeURIComponent(region)}` : '';

      const endpoints = [
        { key: 'capacity',        url: `/bookings/calendar-capacity${regionParam}`,            setter: setCalendarCapacityData },
        { key: 'kpt',             url: `/bookings/kpt-history?days=7${regionParamAmp}`,         setter: setKptHistoryData },
        { key: 'bookings',        url: `/bookings/new-this-week${regionParam}`,                 setter: setNewBookingsData },
        { key: 'cancellation',    url: `/bookings/cancellation-rate${regionParam}`,             setter: setCancellationData },
        { key: 'spend',           url: `/spend/current-week${regionParam}`,                     setter: setSpendData },
        { key: 'coverage',        url: `/orders/coverage-per-day${regionParam}`,                setter: setCoveragePerDayData },
        { key: 'profit',          url: `/marketing/profitability${regionParam}`,                setter: setProfitabilityData },
        { key: 'profit-monthly',  url: `/marketing/profitability-history${regionParam}`,        setter: setProfitabilityMonthlyData },
      ];

      const results = await Promise.allSettled(endpoints.map(e => fetchOne(e.url)));

      const failed = [];
      results.forEach((r, i) => {
        const ep = endpoints[i];
        if (r.status === 'fulfilled') {
          ep.setter(r.value.data);
        } else {
          failed.push(ep.key);
          // 401 håndteres separat
          if (r.reason?.response?.status === 401) {
            navigate('/login');
          }
        }
      });
      if (failed.length > 0) {
        setFetchWarnings(failed);
        console.warn('Booking Pipeline: kunne ikke hente', failed);
      }
    } catch (err) {
      console.error('Data fetch error (unexpected):', err);
    } finally {
      setLoading(false);
    }
  }, [region, navigate, fetchOne]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      navigate('/login');
    } catch (err) {
      navigate('/login');
    }
  };

  // Refetch kun marketing-vs-coverage når periode skifter (uden at trække alle andre endpoints igen)
  useEffect(() => {
    const fetchMktCov = async () => {
      try {
        const regionParamAmp = region && region !== 'Hele landet' ? `&region=${encodeURIComponent(region)}` : '';
        const weeks = (() => { const d=new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate()+3-(d.getDay()+6)%7); const w1=new Date(d.getFullYear(),0,4); return 1+Math.round(((d-w1)/86400000-3+(w1.getDay()+6)%7)/7); })();
        const res = await api.get(`/bookings/marketing-vs-coverage-history?period=${mktCovPeriod}&weeks=${weeks}${regionParamAmp}`);
        setMarketingVsCoverageData(res.data);
      } catch (e) {
        // ignore
      }
    };
    fetchMktCov();
  }, [mktCovPeriod, region]);

  // Memoized data calculations
  // Toggle mellem kommende 4 uger og tidligere 4 uger (tidligere uger vises i kronologisk rækkefølge: ældst først)
  const thisWeek = showPastWeeks
    ? (calendarCapacityData?.prev_week_4 || {})
    : (calendarCapacityData?.this_week || {});
  const nextWeek = showPastWeeks
    ? (calendarCapacityData?.prev_week_3 || {})
    : (calendarCapacityData?.next_week || {});
  const card3Data = showPastWeeks
    ? (calendarCapacityData?.prev_week_2 || {})
    : (calendarCapacityData?.week_3 || {});
  const card4Data = showPastWeeks
    ? (calendarCapacityData?.prev_week_1 || {})
    : (calendarCapacityData?.week_4 || {});
  const card1Label = showPastWeeks ? 'For 4 uger siden' : 'Denne uge';
  const card2Label = showPastWeeks ? 'For 3 uger siden' : 'Næste uge';
  const card3Label = showPastWeeks ? 'For 2 uger siden' : 'Om 2 uger';
  const card4Label = showPastWeeks ? 'Sidste uge' : 'Om 3 uger';
  
  const nyeBookingerChartData = useMemo(() => {
    const defaultData = [
      { dag: 'Man', akkumuleret: null, maal: 36 },
      { dag: 'Tir', akkumuleret: null, maal: 72 },
      { dag: 'Ons', akkumuleret: null, maal: 108 },
      { dag: 'Tor', akkumuleret: null, maal: 144 },
      { dag: 'Fre', akkumuleret: null, maal: 180 },
      { dag: 'Lør', akkumuleret: null, maal: 216 },
      { dag: 'Søn', akkumuleret: null, maal: 252 }
    ];
    
    if (!newBookingsData?.daily_data) return defaultData;
    
    return newBookingsData.daily_data.map(day => ({
      dag: day.dag,
      akkumuleret: day.akkumuleret,
      maal: day.maal
    }));
  }, [newBookingsData]);

  // Data for daglige nye bookinger (ikke akkumuleret)
  const dagligeNyeBookingerData = useMemo(() => {
    const defaultData = [
      { dag: 'Man', nye_bookinger: null },
      { dag: 'Tir', nye_bookinger: null },
      { dag: 'Ons', nye_bookinger: null },
      { dag: 'Tor', nye_bookinger: null },
      { dag: 'Fre', nye_bookinger: null },
      { dag: 'Lør', nye_bookinger: null },
      { dag: 'Søn', nye_bookinger: null }
    ];
    
    if (!newBookingsData?.daily_data) return defaultData;
    
    return newBookingsData.daily_data.map(day => ({
      dag: day.dag,
      nye_bookinger: day.nye_bookinger ?? null
    }));
  }, [newBookingsData]);

  const weeklyBookingTarget = newBookingsData?.weekly_target || 252;
  const pricePerBooking = newBookingsData?.price_per_booking || 0;
  
  // Today's bookings (from backend, actual today's data)
  const todaysBookings = newBookingsData?.today_bookings ?? 0;

  // Booking flow data
  const bookingFlowData = useMemo(() => {
    // Nye bookinger til og med i går (ekskl. i dag) for fair sammenligning med afviklede
    return {
      nyeBookinger: calendarCapacityData?.this_week?.nye_bookinger_oprettet || 0,
      afvikledeBookinger: calendarCapacityData?.this_week?.bookinger_to_yesterday || 0
    };
  }, [calendarCapacityData]);

  // Last week booking flow data
  // Speedometer viser: afviklede bookinger / nye bookinger oprettet
  const lastWeekBookingFlowData = useMemo(() => {
    return {
      nyeBookinger: calendarCapacityData?.last_week?.nye_bookinger_oprettet || 0,  // Nye bookinger oprettet (max værdi)
      afvikledeBookinger: calendarCapacityData?.last_week?.bookinger || 0,  // Afviklede bookinger (gauge værdi)
      weekNumber: calendarCapacityData?.last_week?.week_number || 0
    };
  }, [calendarCapacityData]);

  // KPT history data
  const kptHistory = useMemo(() => ({
    data: kptHistoryData?.history || [],
    currentKpt: calendarCapacityData?.thirty_days_forward?.kpt || 0,
    targetKpt: kptHistoryData?.target_kpt || 0.45,
    snapshotsAvailable: kptHistoryData?.snapshots_available || 0
  }), [kptHistoryData, calendarCapacityData]);

  // Gap calculation (inkl. i dag)
  const nyeBookingerInklIdag = calendarCapacityData?.this_week?.nye_bookinger_oprettet_all || 0;
  const gapData = useMemo(() => {
    const daysIntoWeek = new Date().getDay() || 7;
    const expectedByNow = Math.round((weeklyBookingTarget / 7) * daysIntoWeek);
    const gap = (nyeBookingerInklIdag || 0) - expectedByNow;
    return { gap, expectedByNow };
  }, [weeklyBookingTarget, nyeBookingerInklIdag]);

  // Cancellation data
  const cancellation = useMemo(() => ({
    rate: cancellationData?.cancellation_rate || 0,
    total: cancellationData?.total_bookings || 0,
    cancelled: cancellationData?.cancelled_bookings || 0,
    byRegion: cancellationData?.by_region || {},
    regionFilter: cancellationData?.region_filter || 'all'
  }), [cancellationData]);

  const cancellationColor = cancellation.rate <= 5 ? '#2E7D32' : cancellation.rate <= 10 ? '#C77700' : '#B3261E';

  // Spend data for current week
  const spendChartData = useMemo(() => {
    if (!spendData?.daily_data) return [];
    return spendData.daily_data;
  }, [spendData]);

  const totalWeeklySpend = spendData?.total_spend || 0;
  const avgDailySpend = spendData?.avg_daily_spend || 0;

  // Cost per booking data - combines spend and new bookings
  const costPerBookingData = useMemo(() => {
    if (!spendData?.daily_data || !newBookingsData?.daily_data) return [];
    
    // Match by date instead of day name since we're using rolling 7-day periods
    return spendData.daily_data.map(spendDay => {
      const bookingDay = newBookingsData.daily_data.find(d => d.dato === spendDay.date);
      
      const spend = spendDay?.spend;
      const bookinger = bookingDay?.nye_bookinger || 0;
      
      // Calculate cost per booking (only if we have both values and bookings > 0)
      let costPerBooking = null;
      if (spend !== null && spend !== undefined && bookinger > 0) {
        costPerBooking = Math.round(spend / bookinger);
      }
      
      return {
        dag: spendDay.dag,
        date: spendDay.date,
        spend: spend,
        bookinger: bookinger,
        costPerBooking
      };
    });
  }, [spendData, newBookingsData]);

  // Calculate average cost per booking for the week (excluding today and yesterday since spend data updates late)
  const avgCostPerBooking = useMemo(() => {
    if (!costPerBookingData || costPerBookingData.length === 0) return 0;
    
    // Get today's and yesterday's dates
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const todayStr = today.toISOString().split('T')[0];
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    
    // Filter out today, yesterday and days without valid data
    const validDays = costPerBookingData.filter(d => 
      d.date !== todayStr && 
      d.date !== yesterdayStr &&
      d.spend !== null && 
      d.spend > 0 && 
      d.bookinger > 0
    );
    
    if (validDays.length === 0) return 0;
    
    const totalSpendExcl = validDays.reduce((sum, d) => sum + d.spend, 0);
    const totalBookingsExcl = validDays.reduce((sum, d) => sum + d.bookinger, 0);
    
    if (totalBookingsExcl === 0) return 0;
    
    return Math.round(totalSpendExcl / totalBookingsExcl);
  }, [costPerBookingData]);

  // Coverage per completed booking data
  const coverageChartData = useMemo(() => {
    if (!coveragePerDayData?.daily_data) return [];
    return coveragePerDayData.daily_data;
  }, [coveragePerDayData]);

  const avgCoveragePerBooking = coveragePerDayData?.avg_coverage_per_booking || 0;
  const totalCompletedBookings = coveragePerDayData?.total_bookings || 0;
  const totalCoverage = coveragePerDayData?.total_coverage || 0;

  return (
    <div className="min-h-screen bg-[#121212]" data-testid="booking-pipeline">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#121212]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1920px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Navigation />
              <h1 className="text-xl font-semibold text-white">Booking Pipeline</h1>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="bg-[#1A1A1A] border border-white/10 text-white text-sm px-4 py-2 rounded-lg focus:outline-none focus:border-[#1E6F7A]"
              >
                {regions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <button onClick={handleRefresh} disabled={refreshing} className="p-2 bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-white rounded-lg transition-colors disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={handleLogout} className="p-2 bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-[#B3261E] rounded-lg transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1920px] mx-auto px-6 py-6 space-y-8">
        
        {fetchWarnings.length > 0 && !loading && (
          <div
            data-testid="partial-data-warning"
            className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <span className="text-amber-300 text-sm">
                Delvis data — {fetchWarnings.length} af 8 kilder svarede ikke i tide (Google Sheets kan være langsom).
              </span>
              <span className="text-amber-300/60 text-xs">
                Fejlede: {fetchWarnings.join(', ')}
              </span>
            </div>
            <button
              data-testid="retry-fetch-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-xs bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {refreshing ? 'Prøver igen…' : 'Prøv igen'}
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-white/60">Indlæser data...</div>
          </div>
        ) : (
          <>
            {/* Ugeoversigt */}
            <section>
              <h2 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10 flex items-center justify-between">
                <span>
                  Ugeoversigt
                  {calendarCapacityData?.region_filter && calendarCapacityData.region_filter !== 'all' && (
                    <span className="text-sm font-normal text-[#1E6F7A] ml-2">({calendarCapacityData.region_filter})</span>
                  )}
                </span>
                <button
                  data-testid="toggle-past-weeks-btn"
                  onClick={() => setShowPastWeeks(v => !v)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${showPastWeeks ? 'bg-[#1E6F7A] border-[#1E6F7A] text-white' : 'bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white'}`}
                >
                  {showPastWeeks ? '← Vis kommende 4 uger' : 'Vis tidligere 4 uger →'}
                </button>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {/* Denne Uge */}
                <div className="bg-[#1A1A1A] rounded-lg p-4 border-l-4 border-[#1E6F7A]">
                  <h3 className="text-sm font-medium text-white/60 mb-3">{card1Label}</h3>
                  <p className="text-2xl font-bold text-white">Uge {thisWeek.week_number || '?'}</p>
                  <p className="text-xs text-white/40 mb-2">
                    {thisWeek.week_start && thisWeek.week_end 
                      ? `${new Date(thisWeek.week_start).toLocaleDateString('da-DK', {day: 'numeric', month: 'short'})} - ${new Date(thisWeek.week_end).toLocaleDateString('da-DK', {day: 'numeric', month: 'short'})}`
                      : ''}
                  </p>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/60">Bookinger</span>
                      <span className="text-white font-medium">{thisWeek.bookinger || 0} / {thisWeek.kapacitet || 0}</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ 
                        width: `${thisWeek.belaegning || 0}%`,
                        backgroundColor: (thisWeek.belaegning || 0) >= 80 ? '#2E7D32' : (thisWeek.belaegning || 0) >= 60 ? '#C77700' : '#B3261E'
                      }} />
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span className="text-white/40">{thisWeek.ledige_tider || 0} ledige</span>
                      <span className="font-medium" style={{ color: (thisWeek.belaegning || 0) >= 80 ? '#2E7D32' : (thisWeek.belaegning || 0) >= 60 ? '#C77700' : '#B3261E' }}>
                        {Math.round(thisWeek.belaegning || 0)}%
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 pt-3 border-t border-white/10">
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Timer</span>
                      <span className="text-sm font-medium text-white">{Math.round(thisWeek.timer || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">KPT</span>
                      <span className="text-sm font-medium" style={{ color: (thisWeek.kpt || 0) >= 0.45 ? '#2E7D32' : (thisWeek.kpt || 0) >= 0.35 ? '#C77700' : '#B3261E' }}>
                        {(thisWeek.kpt || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Besøg + kørsel + mont.</span>
                      <span className="text-sm font-medium text-white">
                        {thisWeek.time_usage_pct || 0}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Andet</span>
                      <span className="text-sm font-medium text-white">
                        {thisWeek.andet_pct || 0}%
                      </span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-white/5">
                      <p className="text-xs text-white/40 mb-1">Est. dækning</p>
                      <div className="flex justify-between">
                        <span className="text-xs text-white/60">Fra timer</span>
                        <span className="text-xs font-medium text-white">{formatDKK(Math.round(thisWeek.timer || 0) * 1800)} kr</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-white/60">Fra bookinger</span>
                        <span className="text-xs font-medium text-white">{formatDKK((thisWeek.bookinger || 0) * 4500)} kr</span>
                      </div>
                      {thisWeek.realiseret_daekning !== undefined && (
                        <div className="flex justify-between mt-1 pt-1 border-t border-white/5">
                          <span className="text-xs text-white/60">Realiseret</span>
                          <span className="text-xs font-semibold text-[#2E7D32]">{formatDKK(Math.round(thisWeek.realiseret_daekning || 0))} kr</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Næste Uge */}
                <div className="bg-[#1A1A1A] rounded-lg p-4 border-l-4 border-[#1E6F7A]/60">
                  <h3 className="text-sm font-medium text-white/60 mb-3">{card2Label}</h3>
                  <p className="text-2xl font-bold text-white">Uge {nextWeek.week_number || '?'}</p>
                  <p className="text-xs text-white/40 mb-2">
                    {nextWeek.week_start && nextWeek.week_end 
                      ? `${new Date(nextWeek.week_start).toLocaleDateString('da-DK', {day: 'numeric', month: 'short'})} - ${new Date(nextWeek.week_end).toLocaleDateString('da-DK', {day: 'numeric', month: 'short'})}`
                      : ''}
                  </p>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/60">Bookinger</span>
                      <span className="text-white font-medium">{nextWeek.bookinger || 0} / {nextWeek.kapacitet || 0}</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ 
                        width: `${nextWeek.belaegning || 0}%`,
                        backgroundColor: (nextWeek.belaegning || 0) >= 80 ? '#2E7D32' : (nextWeek.belaegning || 0) >= 60 ? '#C77700' : '#B3261E'
                      }} />
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span className="text-white/40">{nextWeek.ledige_tider || 0} ledige</span>
                      <span className="font-medium" style={{ color: (nextWeek.belaegning || 0) >= 80 ? '#2E7D32' : (nextWeek.belaegning || 0) >= 60 ? '#C77700' : '#B3261E' }}>
                        {Math.round(nextWeek.belaegning || 0)}%
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 pt-3 border-t border-white/10">
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Timer</span>
                      <span className="text-sm font-medium text-white">{Math.round(nextWeek.timer || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">KPT</span>
                      <span className="text-sm font-medium" style={{ color: (nextWeek.kpt || 0) >= 0.45 ? '#2E7D32' : (nextWeek.kpt || 0) >= 0.35 ? '#C77700' : '#B3261E' }}>
                        {(nextWeek.kpt || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Besøg + kørsel + mont.</span>
                      <span className="text-sm font-medium text-white">
                        {nextWeek.time_usage_pct || 0}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Andet</span>
                      <span className="text-sm font-medium text-white">
                        {nextWeek.andet_pct || 0}%
                      </span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-white/5">
                      <p className="text-xs text-white/40 mb-1">Est. dækning</p>
                      <div className="flex justify-between">
                        <span className="text-xs text-white/60">Fra timer</span>
                        <span className="text-xs font-medium text-white">{formatDKK(Math.round(nextWeek.timer || 0) * 1800)} kr</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-white/60">Fra bookinger</span>
                        <span className="text-xs font-medium text-white">{formatDKK((nextWeek.bookinger || 0) * 4500)} kr</span>
                      </div>
                      {nextWeek.realiseret_daekning !== undefined && (
                        <div className="flex justify-between mt-1 pt-1 border-t border-white/5">
                          <span className="text-xs text-white/60">Realiseret</span>
                          <span className="text-xs font-semibold text-[#2E7D32]">{formatDKK(Math.round(nextWeek.realiseret_daekning || 0))} kr</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Uge 3 */}
                <div className="bg-[#1A1A1A] rounded-lg p-4 border-l-4 border-white/20">
                  <h3 className="text-sm font-medium text-white/60 mb-3">{card3Label}</h3>
                  <p className="text-2xl font-bold text-white">Uge {card3Data?.week_number || '?'}</p>
                  <p className="text-xs text-white/40 mb-2">
                    {card3Data?.week_start && card3Data?.week_end 
                      ? `${new Date(card3Data.week_start).toLocaleDateString('da-DK', {day: 'numeric', month: 'short'})} - ${new Date(card3Data.week_end).toLocaleDateString('da-DK', {day: 'numeric', month: 'short'})}`
                      : ''}
                  </p>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/60">Bookinger</span>
                      <span className="text-white font-medium">{card3Data?.bookinger || 0} / {card3Data?.kapacitet || 0}</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ 
                        width: `${card3Data?.belaegning || 0}%`,
                        backgroundColor: (card3Data?.belaegning || 0) >= 80 ? '#2E7D32' : (card3Data?.belaegning || 0) >= 60 ? '#C77700' : '#B3261E'
                      }} />
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span className="text-white/40">{card3Data?.ledige_tider || 0} ledige</span>
                      <span className="font-medium" style={{ color: (card3Data?.belaegning || 0) >= 80 ? '#2E7D32' : (card3Data?.belaegning || 0) >= 60 ? '#C77700' : '#B3261E' }}>
                        {Math.round(card3Data?.belaegning || 0)}%
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 pt-3 border-t border-white/10">
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Timer</span>
                      <span className="text-sm font-medium text-white">{Math.round(card3Data?.timer || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">KPT</span>
                      <span className="text-sm font-medium" style={{ color: (card3Data?.kpt || 0) >= 0.45 ? '#2E7D32' : (card3Data?.kpt || 0) >= 0.35 ? '#C77700' : '#B3261E' }}>
                        {(card3Data?.kpt || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Besøg + kørsel + mont.</span>
                      <span className="text-sm font-medium text-white">
                        {card3Data?.time_usage_pct || 0}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Andet</span>
                      <span className="text-sm font-medium text-white">
                        {card3Data?.andet_pct || 0}%
                      </span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-white/5">
                      <p className="text-xs text-white/40 mb-1">Est. dækning</p>
                      <div className="flex justify-between">
                        <span className="text-xs text-white/60">Fra timer</span>
                        <span className="text-xs font-medium text-white">{formatDKK(Math.round(card3Data?.timer || 0) * 1800)} kr</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-white/60">Fra bookinger</span>
                        <span className="text-xs font-medium text-white">{formatDKK((card3Data?.bookinger || 0) * 4500)} kr</span>
                      </div>
                      {card3Data?.realiseret_daekning !== undefined && (
                        <div className="flex justify-between mt-1 pt-1 border-t border-white/5">
                          <span className="text-xs text-white/60">Realiseret</span>
                          <span className="text-xs font-semibold text-[#2E7D32]">{formatDKK(Math.round(card3Data.realiseret_daekning || 0))} kr</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Uge 4 */}
                <div className="bg-[#1A1A1A] rounded-lg p-4 border-l-4 border-white/10">
                  <h3 className="text-sm font-medium text-white/60 mb-3">{card4Label}</h3>
                  <p className="text-2xl font-bold text-white">Uge {card4Data?.week_number || '?'}</p>
                  <p className="text-xs text-white/40 mb-2">
                    {card4Data?.week_start && card4Data?.week_end 
                      ? `${new Date(card4Data.week_start).toLocaleDateString('da-DK', {day: 'numeric', month: 'short'})} - ${new Date(card4Data.week_end).toLocaleDateString('da-DK', {day: 'numeric', month: 'short'})}`
                      : ''}
                  </p>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/60">Bookinger</span>
                      <span className="text-white font-medium">{card4Data?.bookinger || 0} / {card4Data?.kapacitet || 0}</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ 
                        width: `${card4Data?.belaegning || 0}%`,
                        backgroundColor: (card4Data?.belaegning || 0) >= 80 ? '#2E7D32' : (card4Data?.belaegning || 0) >= 60 ? '#C77700' : '#B3261E'
                      }} />
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span className="text-white/40">{card4Data?.ledige_tider || 0} ledige</span>
                      <span className="font-medium" style={{ color: (card4Data?.belaegning || 0) >= 80 ? '#2E7D32' : (card4Data?.belaegning || 0) >= 60 ? '#C77700' : '#B3261E' }}>
                        {Math.round(card4Data?.belaegning || 0)}%
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 pt-3 border-t border-white/10">
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Timer</span>
                      <span className="text-sm font-medium text-white">{Math.round(card4Data?.timer || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">KPT</span>
                      <span className="text-sm font-medium" style={{ color: (card4Data?.kpt || 0) >= 0.45 ? '#2E7D32' : (card4Data?.kpt || 0) >= 0.35 ? '#C77700' : '#B3261E' }}>
                        {(card4Data?.kpt || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Besøg + kørsel + mont.</span>
                      <span className="text-sm font-medium text-white">
                        {card4Data?.time_usage_pct || 0}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-white/60">Andet</span>
                      <span className="text-sm font-medium text-white">
                        {card4Data?.andet_pct || 0}%
                      </span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-white/5">
                      <p className="text-xs text-white/40 mb-1">Est. dækning</p>
                      <div className="flex justify-between">
                        <span className="text-xs text-white/60">Fra timer</span>
                        <span className="text-xs font-medium text-white">{formatDKK(Math.round(card4Data?.timer || 0) * 1800)} kr</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-white/60">Fra bookinger</span>
                        <span className="text-xs font-medium text-white">{formatDKK((card4Data?.bookinger || 0) * 4500)} kr</span>
                      </div>
                      {card4Data?.realiseret_daekning !== undefined && (
                        <div className="flex justify-between mt-1 pt-1 border-t border-white/5">
                          <span className="text-xs text-white/60">Realiseret</span>
                          <span className="text-xs font-semibold text-[#2E7D32]">{formatDKK(Math.round(card4Data.realiseret_daekning || 0))} kr</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Kapacitet & Behov */}
            <section>
              <h2 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-white/10">
                Kapacitet & Behov
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-4">
                {/* Nye bookinger i denne uge (akkumuleret) */}
                <SectionCard 
                  title="Nye bookinger (sidste 7 dage)"
                  tooltip={
                    <InfoTooltip
                      title="Nye bookinger (akkumuleret)"
                      description="Viser det akkumulerede antal nye bookinger oprettet de sidste 7 dage sammenlignet med ugentligt mål."
                      formula="Akkumuleret = Sum af nye bookinger fra dag 1 til aktuel dag"
                      details={[
                        "Blå søjler = Faktisk resultat",
                        "Rød linje = Ugentligt mål (252)",
                        "Baseret på opmaling_bookings_created"
                      ]}
                      dataSource="Leadvalidator Export"
                    />
                  }
                >
                  <NyeBookingerChart data={nyeBookingerChartData} weeklyTarget={weeklyBookingTarget} />
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/10">
                    <span className="text-xs text-white/60">{formatDKK(Math.round(pricePerBooking))} kr pr. booking</span>
                    <div className="text-right">
                      <span className="text-xs font-medium text-white">{todaysBookings} nye i dag</span>
                      <p className="text-xs text-white/60">{bookingFlowData.nyeBookinger} i alt denne uge</p>
                    </div>
                  </div>
                </SectionCard>

                {/* Nye bookinger pr. dag */}
                <SectionCard 
                  title="Nye bookinger pr. dag (sidste 7 dage)"
                  tooltip={
                    <InfoTooltip
                      title="Nye bookinger pr. dag"
                      description="Viser antal nye bookinger oprettet pr. dag de sidste 7 dage."
                      formula="Dagligt mål = 252 ÷ 7 ≈ 36 bookinger"
                      details={[
                        "Grøn: ≥ dagligt mål (36)",
                        "Orange: 70-100% af mål",
                        "Rød: < 70% af mål",
                        "Rød linje = Dagligt mål"
                      ]}
                      dataSource="Leadvalidator Export"
                    />
                  }
                >
                  <DagligeNyeBookingerChart data={dagligeNyeBookingerData} dailyTarget={newBookingsData?.daily_target || 36} />
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/10">
                    <span className="text-xs text-white/60">Dagligt mål: ~{Math.round(newBookingsData?.daily_target || 36)}</span>
                    <div className="text-right">
                      <span className="text-xs font-medium text-white">{todaysBookings} i dag</span>
                    </div>
                  </div>
                </SectionCard>
                
                {/* Bookingflow */}
                <SectionCard 
                  title="Bookingflow"
                  tooltip={
                    <InfoTooltip
                      title="Bookingflow"
                      description="Viser forskellen mellem nye bookinger oprettet og afviklede opmålinger."
                      formula="Flow = Nye bookinger - Afviklede bookinger"
                      details={[
                        "Grøn: Flow ≥ +10 (bygger pipeline)",
                        "Orange: Flow 0 til +10 (stabilt)",
                        "Rød: Flow < 0 (bruger pipeline)",
                        "Sammenligner denne uge med sidste uge"
                      ]}
                      dataSource="CalendarTypeCounts"
                    />
                  }
                >
                  <div className="flex flex-col items-center">
                    <div className="flex justify-around w-full">
                      <div className="text-center">
                        <p className="text-xs text-white/60 mb-2">Sidste uge</p>
                        <BookingFlowGauge 
                          nyeBookinger={lastWeekBookingFlowData.nyeBookinger} 
                          afvikledeBookinger={lastWeekBookingFlowData.afvikledeBookinger} 
                          size={120}
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-white/60 mb-2">Denne uge</p>
                        <BookingFlowGauge 
                          nyeBookinger={bookingFlowData.nyeBookinger} 
                          afvikledeBookinger={bookingFlowData.afvikledeBookinger} 
                          size={120}
                        />
                      </div>
                    </div>
                    <div className="w-full mt-2 pt-2 border-t border-white/10">
                      <div className="flex justify-between text-xs">
                        <div>
                          <span className="text-white/60">Sidste: </span>
                          <span className="text-white font-medium">{lastWeekBookingFlowData.afvikledeBookinger}</span>
                          <span className="text-white/40"> afv. / </span>
                          <span className="text-white font-medium">{lastWeekBookingFlowData.nyeBookinger}</span>
                          <span className="text-white/40"> nye</span>
                        </div>
                        <div>
                          <span className="text-white/60">Denne uge: </span>
                          <span className="text-white font-medium">{bookingFlowData.nyeBookinger}</span>
                          <span className="text-white/40"> nye / </span>
                          <span className="text-white font-medium">{bookingFlowData.afvikledeBookinger}</span>
                          <span className="text-white/40"> afv.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </SectionCard>
                
                {/* Ugentligt behov */}
                <SectionCard 
                  title="Ugentligt behov"
                  tooltip={
                    <InfoTooltip
                      title="Ugentligt behov"
                      description="Viser fremskridt mod det ugentlige booking-mål og gap mellem faktisk og forventet."
                      formula="Gap = Faktiske bookinger - Forventet på dette tidspunkt"
                      details={[
                        "Ugentligt mål: 252 bookinger",
                        "Forventet beregnes baseret på dage i ugen",
                        "Positivt gap = Foran målsætning",
                        "Negativt gap = Bagud på målsætning"
                      ]}
                      dataSource="Beregnet fra bookinger"
                    />
                  }
                >
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-white/60">Ugentligt mål</span>
                        <span className="text-lg font-bold text-white">{weeklyBookingTarget}</span>
                      </div>
                      <p className="text-xs text-white/40">bookinger pr. uge</p>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-white/60">Nuværende tempo</span>
                        <span className="text-lg font-bold text-white">{nyeBookingerInklIdag || 0}</span>
                      </div>
                      <p className="text-xs text-white/40">nye bookinger indtil nu</p>
                    </div>
                    <div className="pt-3 border-t border-white/10">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-white/60">Gap vs. forventet</span>
                        <span className="text-lg font-bold" style={{ color: gapData.gap >= 0 ? '#2E7D32' : '#B3261E' }}>
                          {gapData.gap >= 0 ? '+' : ''}{gapData.gap}
                        </span>
                      </div>
                      <p className="text-xs text-white/40">Forventet nu: {gapData.expectedByNow} bookinger</p>
                    </div>
                  </div>
                </SectionCard>
                
                {/* Aflysningsprocent */}
                <SectionCard 
                  title="Aflysningsprocent (30 dage)"
                  tooltip={
                    <InfoTooltip
                      title="Aflysningsprocent"
                      description="Viser andelen af bookinger der er blevet aflyst de sidste 30 dage."
                      formula="Aflysning % = (Aflyste bookinger ÷ Totale bookinger) × 100"
                      details={[
                        "Grøn: ≤ 5% (meget godt)",
                        "Orange: 5-10% (acceptabelt)",
                        "Rød: > 10% (problem)",
                        "Inkluderer kun aflyste, ikke ombookede"
                      ]}
                      dataSource="Bookinger sheet"
                    />
                  }
                >
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className="text-4xl font-bold" style={{ color: cancellationColor }}>{cancellation.rate}%</p>
                      <p className="text-xs text-white/40 mt-1">aflysninger</p>
                    </div>
                    <div className="pt-3 border-t border-white/10">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-white/60">Bookinger i alt</span>
                        <span className="text-white font-medium">{cancellation.total}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm mt-1">
                        <span className="text-white/60">Aflyst</span>
                        <span className="font-medium" style={{ color: cancellationColor }}>{cancellation.cancelled}</span>
                      </div>
                    </div>
                    {cancellation.regionFilter === 'all' && Object.keys(cancellation.byRegion).length > 0 && (
                      <></>
                    )}
                  </div>
                </SectionCard>
              </div>
              
              {/* Marketing Spend og Pris pr. booking - side om side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                {/* Marketing Spend - sidste 7 dage */}
                <SectionCard 
                  title={`Marketingudgifter (${spendData?.period_label || 'Sidste 7 dage'})${spendData?.region_filter && spendData.region_filter !== 'all' ? ` - ${spendData.region_filter}` : ''}`}
                  tooltip={
                    <InfoTooltip
                      title="Marketing Spend"
                      description="Viser dagligt marketing spend de sidste 7 dage (rullende periode)."
                      formula="Total = Sum af dagligt spend"
                      details={[
                        "Inkluderer alle marketingkanaler",
                        "Facebook, Google, osv.",
                        "Bruges til at beregne pris pr. booking"
                      ]}
                      dataSource="Import_Spend _pr._dag"
                    />
                  }
                >
                  <SpendChart data={spendChartData} totalSpend={totalWeeklySpend} />
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/10">
                    <div>
                      <p className="text-xs text-white/60">Total sidste 7 dage</p>
                      <p className="text-lg font-bold text-[#C77700]">{formatDKK(totalWeeklySpend)} kr</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/60">Gns. pr. dag</p>
                      <p className="text-sm font-medium text-white">{formatDKK(avgDailySpend)} kr</p>
                    </div>
                  </div>
                </SectionCard>
                
                {/* Pris pr. ny booking */}
                <SectionCard 
                  title={`Pris pr. ny booking (${spendData?.period_label || 'Sidste 7 dage'})${spendData?.region_filter && spendData.region_filter !== 'all' ? ` - ${spendData.region_filter}` : ''}`}
                  tooltip={
                    <InfoTooltip
                      title="Pris pr. ny booking"
                      description="Viser hvor meget det koster at få én ny booking baseret på marketing spend."
                      formula="Pris pr. booking = Marketing spend ÷ Nye bookinger"
                      details={[
                        "Grøn: ≤ 1.200 kr (godt)",
                        "Orange: 1.200-1.500 kr (acceptabelt)",
                        "Rød: > 1.500 kr (for dyrt)",
                        "Ekskluderer i dag og i går (forsinket data)"
                      ]}
                      dataSource="Beregnet fra spend og bookinger"
                    />
                  }
                >
                  <CostPerBookingChart data={costPerBookingData} />
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/10">
                    <div>
                      <p className="text-xs text-white/60">Gns. pris pr. booking (ekskl. i går+i dag)</p>
                      <p className="text-lg font-bold" style={{ color: avgCostPerBooking <= 1200 ? '#2E7D32' : avgCostPerBooking <= 1500 ? '#C77700' : '#B3261E' }}>
                        {avgCostPerBooking > 0 ? `${formatDKK(avgCostPerBooking)} kr` : 'N/A'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/60">Denne uge (ekskl. i går+i dag)</p>
                      <p className="text-sm font-medium text-white">
                        {(() => {
                          const today = new Date();
                          const yesterday = new Date(today);
                          yesterday.setDate(yesterday.getDate() - 1);
                          const todayStr = today.toISOString().split('T')[0];
                          const yesterdayStr = yesterday.toISOString().split('T')[0];
                          const validDays = costPerBookingData.filter(d => d.date !== todayStr && d.date !== yesterdayStr && d.spend !== null && d.spend > 0);
                          const spendExcl = validDays.reduce((sum, d) => sum + d.spend, 0);
                          const bookingsExcl = validDays.reduce((sum, d) => sum + d.bookinger, 0);
                          return `${formatDKK(spendExcl)} kr / ${bookingsExcl} bookinger`;
                        })()}
                      </p>
                    </div>
                  </div>
                </SectionCard>
              </div>
              
              {/* KPT Historik og Marketing Profitabilitet */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                <SectionCard 
                  title="KPT Historik (30 dage frem)"
                  tooltip={
                    <InfoTooltip
                      title="KPT Historik (30 dage frem)"
                      description="Viser udviklingen i KPT (Kunder pr. time) over de sidste 7 dages snapshots."
                      formula="KPT = Opmålinger ÷ Arbejdstimer"
                      details={[
                        "Grøn: KPT ≥ 0,45 (godt)",
                        "Orange: KPT 0,35-0,45",
                        "Rød: KPT < 0,35",
                        "Baseret på de næste 30 dages bookinger"
                      ]}
                      dataSource="CalendarTypeCounts + daglige snapshots"
                    />
                  }
                >
                  <KptHistoryChart 
                    historyData={kptHistory.data}
                    currentKpt={kptHistory.currentKpt}
                    targetKpt={kptHistory.targetKpt}
                    snapshotsAvailable={kptHistory.snapshotsAvailable}
                  />
                  {/* KPT mål 0,20 — mangler/overskud + estimeret marketing-omkostning */}
                  {(() => {
                    const tdf = calendarCapacityData?.thirty_days_forward || {};
                    const bookings = Number(tdf.bookinger || 0);
                    const hours = Number(tdf.timer || 0);
                    const TARGET = 0.20;
                    const targetBookings = Math.round(TARGET * hours);
                    const diff = bookings - targetBookings;
                    const missing = diff < 0 ? Math.abs(diff) : 0;
                    const surplus = diff > 0 ? diff : 0;
                    const ppb = Number(pricePerBooking || 0);
                    const marketingCost = missing * ppb;
                    if (hours <= 0) return null;
                    return (
                      <div data-testid="kpt-target-gap-panel" className="mt-3 p-3 rounded-lg bg-white/[0.03] border border-white/10 text-xs">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white/60">Mål: KPT 0,20 ({region})</span>
                          <span className="text-white/40">{bookings} bookinger / {Math.round(hours)} timer</span>
                        </div>
                        {missing > 0 ? (
                          <>
                            <div className="flex items-center justify-between">
                              <span className="text-white/80">Mangler for at nå målet</span>
                              <span className="font-semibold text-[#B3261E]">{missing} bookinger</span>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-white/60">Mål-bookinger ved 0,20</span>
                              <span className="text-white/80">{targetBookings}</span>
                            </div>
                            {ppb > 0 && (
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                                <span className="text-white/60">Est. marketing-omkostning</span>
                                <span className="font-semibold text-white">{formatDKK(Math.round(marketingCost))} kr</span>
                              </div>
                            )}
                            {ppb > 0 && (
                              <div className="text-[10px] text-white/40 mt-1">
                                ({missing} bookinger × {formatDKK(Math.round(ppb))} kr pr. booking)
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="flex items-center justify-between">
                              <span className="text-white/80">Bookinger i overskud</span>
                              <span className="font-semibold text-[#2E7D32]">+{surplus} bookinger</span>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-white/60">Mål-bookinger ved 0,20</span>
                              <span className="text-white/80">{targetBookings}</span>
                            </div>
                            {ppb > 0 && surplus > 0 && (
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                                <span className="text-white/60">Est. marketing-besparelse</span>
                                <span className="font-semibold text-[#2E7D32]">{formatDKK(Math.round(surplus * ppb))} kr</span>
                              </div>
                            )}
                            {ppb > 0 && surplus > 0 && (
                              <div className="text-[10px] text-white/40 mt-1">
                                ({surplus} bookinger × {formatDKK(Math.round(ppb))} kr pr. booking)
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })()}
                </SectionCard>

                {/* Marketing Profitabilitet */}
                {profitabilityData && !profitabilityData.error ? (
                  <SectionCard 
                    title="Marketing Profitabilitet (sidste 30 dage)"
                    tooltip={
                      <InfoTooltip
                        title="Marketing Profitabilitet"
                        description="Viser den samlede profitabilitet af marketing indsatsen de sidste 30 dage."
                        formula="Profit = Dækning - Konsulent provision - Marketing spend"
                        details={[
                          "Baseret på afviklede opmålinger",
                          "Konsulent provision: 20% af dækning",
                          "Grøn = Positiv profit pr. opmåling",
                          "Rød = Negativ profit pr. opmåling"
                        ]}
                        dataSource="Data_klar + Import_Spend"
                      />
                    }
                  >
                    {/* Hovedtal - Profit pr. opmåling i fokus */}
                    <div className="text-center py-3">
                      <p className="text-xs text-white/50 mb-1">Profit pr. opmåling</p>
                      <p className={`text-4xl font-bold ${(profitabilityData.profitability?.profit_per_booking || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatDKK(profitabilityData.profitability?.profit_per_booking || 0)} kr
                      </p>
                      <p className="text-xs text-white/40 mt-1">efter alle udgifter</p>
                    </div>
                    
                    {/* Footer med key metrics */}
                    <div className="flex justify-between items-center pt-3 border-t border-white/10">
                      <div>
                        <p className="text-xs text-white/60">Marketing kostpris</p>
                        <p className="text-lg font-bold text-[#1E6F7A]">{formatDKK(profitabilityData.profitability?.cost_per_booking || 0)} kr</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-white/60">Netto profit</p>
                        <p className={`text-lg font-bold ${(profitabilityData.profitability?.net_profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatDKK(profitabilityData.profitability?.net_profit || 0)} kr
                        </p>
                      </div>
                    </div>
                    
                    {/* Collapsible beregningsdetaljer */}
                    <button 
                      onClick={() => setProfitabilityExpanded(!profitabilityExpanded)}
                      className="w-full flex items-center justify-center gap-2 mt-3 pt-3 border-t border-white/10 text-white/60 hover:text-white transition-colors"
                    >
                      <span className="text-xs">Vis beregning</span>
                      {profitabilityExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    {profitabilityExpanded && (
                      <div className="space-y-2 pt-3 mt-1 border-t border-white/10 animate-in slide-in-from-top-2 duration-200">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/60">Opmålinger</span>
                          <span className="text-white font-medium">{profitabilityData.bookings?.total || 0}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/60">Samlet dækning</span>
                          <span className="text-white font-medium">{formatDKK(profitabilityData.coverage?.total || 0)} kr</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/60">Konsulentprovision ({profitabilityData.expenses?.consultant_commission_pct || 20}%)</span>
                          <span className="text-red-400 font-medium">-{formatDKK(profitabilityData.expenses?.consultant_commission || 0)} kr</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/60">Marketingudgifter</span>
                          <span className="text-red-400 font-medium">-{formatDKK(profitabilityData.expenses?.marketing_spend || 0)} kr</span>
                        </div>
                        <div className="flex justify-between items-center text-xs text-white/40 pt-2">
                          <span>Periode: {profitabilityData.period?.days || 30} dage</span>
                        </div>
                      </div>
                    )}
                  </SectionCard>
                ) : (
                  <SectionCard title="Marketing Profitabilitet (sidste 30 dage)">
                    <p className="text-white/40 text-sm">Indlæser profitabilitetsdata...</p>
                  </SectionCard>
                )}
              </div>
            </section>

            {/* Marketing % af dækning — uge-for-uge eller måned-for-måned */}
            <section className="mt-4">
              <SectionCard
                title={`Marketing-udgift som % af dækningsbidrag (${mktCovPeriod === 'month' ? 'måned for måned' : 'uge for uge'})`}
                tooltip={
                  <InfoTooltip
                    title="Marketing-udgift som % af dækning"
                    description="Viser hvor stor en del af det realiserede dækningsbidrag der bruges på marketing pr. periode."
                    formula="Pct = Marketingudgift ÷ Dækningsbidrag × 100"
                    details={[
                      "Dækning: sum af dækningsbidrag fra Data_klar (kol AD)",
                      "Status-filter: ekskluder pending og canceled",
                      "Marketing: sum fra Import_Spend_pr._dag (region-filtreret)",
                      "Lavere % = mere effektiv marketingsindsats",
                    ]}
                    dataSource="Data_klar + Import_Spend _pr._dag"
                  />
                }
              >
                <div className="flex items-center justify-end gap-1 mb-3">
                  <button
                    data-testid="mktcov-period-week"
                    onClick={() => setMktCovPeriod('week')}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${mktCovPeriod === 'week' ? 'bg-[#1E6F7A] border-[#1E6F7A] text-white' : 'bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white'}`}
                  >Uge</button>
                  <button
                    data-testid="mktcov-period-month"
                    onClick={() => setMktCovPeriod('month')}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${mktCovPeriod === 'month' ? 'bg-[#1E6F7A] border-[#1E6F7A] text-white' : 'bg-transparent border-white/20 text-white/60 hover:border-white/40 hover:text-white'}`}
                  >Måned</button>
                </div>
                {marketingVsCoverageData?.history?.length ? (
                  <div data-testid="marketing-vs-coverage-chart">
                    <ResponsiveContainer width="100%" height={260}>
                      <ComposedChart data={marketingVsCoverageData.history} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                          axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                          axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                          tickLine={false}
                          tickFormatter={(v) => `${v}%`}
                          domain={[0, (dataMax) => Math.max(50, Math.ceil(dataMax / 10) * 10)]}
                        />
                        <Tooltip
                          contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
                          labelStyle={{ color: 'rgba(255,255,255,0.7)' }}
                          formatter={(value, name, props) => {
                            const item = props?.payload || {};
                            return [
                              `${value}%`,
                              `Marketing ${formatDKK(Math.round(item.marketing_spend || 0))} kr / Dækning ${formatDKK(Math.round(item.daekning || 0))} kr`,
                            ];
                          }}
                        />
                        <ReferenceLine y={30} stroke="rgba(255,255,255,0.25)" strokeDasharray="4 4" label={{ value: 'Mål: 30%', fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
                        <Line
                          type="monotone"
                          dataKey="pct"
                          name="Marketing % af dækning"
                          stroke="#1E6F7A"
                          strokeWidth={2.5}
                          dot={{ fill: '#1E6F7A', r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5 text-xs">
                      <div className="text-white/40">
                        Periode: {marketingVsCoverageData.history[0]?.label} → {marketingVsCoverageData.history[marketingVsCoverageData.history.length - 1]?.label}
                      </div>
                      <div className="text-white/60">
                        Seneste afsluttede {mktCovPeriod === 'month' ? 'måned' : 'uge'}: <span className="font-semibold text-white">{
                          (() => {
                            const h = marketingVsCoverageData.history;
                            const lastClosed = [...h].reverse().find(w => (w.daekning || 0) > 0);
                            return lastClosed ? `${lastClosed.label}: ${lastClosed.pct}%` : '—';
                          })()
                        }</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-white/40 text-sm">Indlæser data...</p>
                )}
              </SectionCard>
            </section>

            {/* AI Analysis & Management Notes - side by side */}
            <section className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AIAnalysisSection region={region} />
                <ManagementNotes />
              </div>
            </section>
          </>
        )}
        
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-4 mt-8">
        <div className="max-w-[1920px] mx-auto px-6">
          <p className="text-center text-xs text-white/60">
            Luxsun Nordic · Booking Pipeline · {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
};
